import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HealthyContactEmbed } from "@/components/site/HealthyContactEmbed";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { SamplingLocationsSection } from "@/components/site/SamplingLocationsSection";
import { useLang } from "@/i18n/LangContext";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock, ArrowRight, Navigation } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";

const MAPS_URL =
  "https://www.google.com/maps/dir//Intygsl%C3%A4karen+J%C3%A4rntorgsgatan+8+413+30+G%C3%B6teborg";
const EMBED_URL =
  "https://www.google.com/maps?q=J%C3%A4rntorgsgatan+8,+413+30+G%C3%B6teborg&output=embed";

const Kontakt = () => {
  const { t, lang } = useLang();
  const bookingHref = lang === "sv" ? "/boka-tid" : "/en/book-appointment";


  const title = lang === "sv" ? "Kontakt | Intygsläkaren" : "Contact | Intygsläkaren";
  const desc = lang === "sv"
    ? "Kontakta Intygsläkaren i Göteborg för bokning av läkarintyg. Ring 031-761 77 00 eller skicka ett meddelande."
    : "Contact Intygsläkaren in Gothenburg to book a medical certificate. Call +46 31-761 77 00 or send us a message.";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        <PageHeroCard compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t.contact.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t.contact.subtitle}
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild variant="hero" size="xl">
                <a href={bookingHref}>
                  {t.contact.bookCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="ghost-outline" size="xl">
                <a href="tel:+46317617700">
                  <Phone className="mr-2 h-4 w-4" />
                  {t.contact.callCta}
                </a>
              </Button>
              <Button asChild variant="ghost-outline" size="xl">
                <a href={MAPS_URL} target="_blank" rel="noreferrer">
                  <Navigation className="mr-2 h-4 w-4" />
                  {t.contact.directionsCta}
                </a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        <section className="py-12 lg:py-16">
          <div className="container-wide">
            <div className="rounded-2xl border border-border/60 bg-card sm:rounded-3xl p-6 shadow-card sm:p-10">
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                {t.contact.formTitle}
              </h2>
              <p className="mt-2 text-muted-foreground">{t.contact.formSubtitle}</p>
              <HealthyContactEmbed
                title={t.contact.formTitle}
                className="mt-8 overflow-hidden rounded-2xl border border-border bg-background"
                iframeClassName="h-[80vh] min-h-[720px] w-full border-0"
              />
            </div>
          </div>
        </section>

        <section className="pb-16 lg:pb-24">
          <div className="container-wide">
            <div className="max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                {t.contact.mapTitle}
              </span>
              <h2 className="mt-2 font-display text-3xl font-semibold text-foreground sm:text-4xl">
                {t.contact.address}
              </h2>
              <p className="mt-3 text-muted-foreground">{t.contact.mapSubtitle}</p>
            </div>
            <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.6fr] lg:items-stretch">
              <aside className="h-full">
                <div className="h-full rounded-2xl border border-border/60 bg-card sm:rounded-3xl p-6 shadow-card sm:p-8">
                  <h3 className="font-display text-xl font-semibold text-foreground">{t.contact.infoTitle}</h3>
                  <ul className="mt-5 space-y-5 text-sm">
                    <li className="flex gap-3">
                      <Phone className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">{t.contact.callDirect}</div>
                        <a href="tel:+46317617700" className="text-muted-foreground hover:text-primary">
                          031-761 77 00
                        </a>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <Mail className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">{t.contact.sendEmail}</div>
                        <a href="mailto:info@intygslakaren.se" className="text-muted-foreground hover:text-primary">
                          info@intygslakaren.se
                        </a>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">{t.contact.visitUs}</div>
                        <div className="text-muted-foreground">{t.contact.address}</div>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <Clock className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium text-foreground">{t.contact.hours}</div>
                        <div className="text-muted-foreground">{t.contact.hoursWeek}</div>
                        <div className="text-muted-foreground">{t.contact.hoursSat}</div>
                        <div className="text-muted-foreground">{t.contact.hoursSun}</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </aside>
              <div className="h-full overflow-hidden rounded-2xl border border-border/60 shadow-card sm:rounded-3xl">
                <iframe
                  title={t.contact.mapTitle}
                  src={EMBED_URL}
                  width="100%"
                  height="420"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="block h-full min-h-[420px] w-full"
                />
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-border/60 bg-card sm:rounded-3xl p-6 shadow-card sm:p-8">
              <h3 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{t.contact.faqTitle}</h3>
              <div className="mt-6 grid gap-4">
                <article className="rounded-2xl border border-border/60 bg-muted/20 p-4 sm:p-5">
                  <h4 className="font-semibold text-foreground">{t.contact.faqTimeQ}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.contact.faqTimeA}</p>
                </article>
                <article className="rounded-2xl border border-border/60 bg-muted/20 p-4 sm:p-5">
                  <h4 className="font-semibold text-foreground">{t.contact.faqBookingQ}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.contact.faqBookingA}</p>
                </article>
                <article className="rounded-2xl border border-border/60 bg-muted/20 p-4 sm:p-5">
                  <h4 className="font-semibold text-foreground">{t.contact.faqPaymentQ}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.contact.faqPaymentA}</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <SamplingLocationsSection className="bg-muted/20" />

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.contact },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Kontakt;
